import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const CreateRoleModal = ({ isOpen, onClose, onCreateRole, existingRoles }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: null
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (existingRoles.some(role => role.name.toLowerCase() === formData.name.toLowerCase())) {
      newErrors.name = 'Role name already exists';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Role description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onCreateRole(formData);
      setFormData({ name: '', description: '', parentId: null });
      setErrors({});
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', parentId: null });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  // Get root roles for parent selection
  const rootRoles = existingRoles.filter(role => !role.parentId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-text-primary">Create New Role</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-md hover:bg-secondary-50 transition-colors duration-150"
          >
            <Icon name="X" size={16} className="text-secondary-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Role Name */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Role Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`
                  w-full px-3 py-2 border rounded-md transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-primary/20
                  ${errors.name 
                    ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                  }
                `}
                placeholder="Enter role name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Role Description */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className={`
                  w-full px-3 py-2 border rounded-md transition-colors duration-150 resize-none
                  focus:outline-none focus:ring-2 focus:ring-primary/20
                  ${errors.description 
                    ? 'border-error focus:border-error' :'border-secondary-200 focus:border-primary'
                  }
                `}
                placeholder="Describe the role's purpose and responsibilities"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Parent Role */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Parent Role (Optional)
              </label>
              <select
                value={formData.parentId || ''}
                onChange={(e) => handleInputChange('parentId', e.target.value || null)}
                className="
                  w-full px-3 py-2 border border-secondary-200 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                  transition-colors duration-150
                "
              >
                <option value="">No parent role</option>
                {rootRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-secondary">
                Child roles inherit permissions from their parent role
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={handleClose}
              className="
                px-4 py-2 border border-secondary-300 text-text-primary rounded-md font-medium
                hover:bg-secondary-50 transition-colors duration-150
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              className="
                px-4 py-2 bg-primary text-white rounded-md font-medium
                hover:bg-primary-700 transition-colors duration-150 hover-lift
                focus:outline-none focus:ring-2 focus:ring-primary/20
                flex items-center gap-2
              "
            >
              <Icon name="Plus" size={16} />
              Create Role
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoleModal;